import { Injectable } from '@angular/core';
import { News } from '../models/news';
import { DatabaseService } from './base/database.service';
import { UserService } from './user.service';
import { ClaimsService } from './claims.service';

@Injectable({
  providedIn: 'root',
})
export class NewsService extends DatabaseService<News> {
  public teste: any;

  constructor(private us: UserService, private cs: ClaimsService) {
    super('cp_news');
  }

  async getNews() {
    const ret = await this.getAll(3, 0);

    return ret;
  }

  async getNewsByNewest(
    config: {
      limit?: number;
      page?: number;
      order?: 'desc' | 'asc';
      orderBy?: string;
    } = {}
  ) {
    const newsOptionsQuery = {
      limit: 10,
      page: 0,
      order: 'desc',
      orderBy: 'created_at',
      ...config,
    };

    const query = this.supabase
      .from(this.table)
      .select()
      .order(newsOptionsQuery.orderBy, {
        ascending: newsOptionsQuery.order === 'asc',
      });
    if (newsOptionsQuery.limit) query.limit(newsOptionsQuery.limit);
    if (newsOptionsQuery.page) {
      const { from, to } = this._getPagination(
        newsOptionsQuery.page,
        newsOptionsQuery.limit
      );
      query.range(from, to);
    }

    const { data, error } = await query;

    return (data as News[]) || [];
  }
  async getNewsByRelatedCase(
    idCase: string,
    limit: number = 3
  ): Promise<News[]> {
    const now = new Date().toISOString();
    const q = this.supabase
      .from(this.table)
      .select()
      .eq('case_id', idCase)
      .eq('status', 'PUBLISHED')
      // valid_from can be null (no start) or <= now
      .or(`valid_from.is.null,valid_from.lte.${now}`)
      // valid_until can be null (no end) or >= now
      .or(`valid_until.is.null,valid_until.gte.${now}`)
      .order('created_at', { ascending: false });
    if (limit) q.limit(limit);

    const { data, error } = await q;
    if (error) throw new Error(error.message);

    return data;
  }

  async getByHighlightPriority(limit: number = 3) {
    const q = this.supabase
      .from(this.table)
      .select()
      .order('metadata->highlight_priority', { ascending: false });
    if (limit) q.limit(limit);

    const { data, error } = await q;
    if (error) throw new Error(error.message);

    return data;
  }

  async getByRelevance(limit: number = 3) {
    const cu = await this.us.getCurrentUser();
    if (!cu || !cu.id) {
      throw new Error('User not loaded');
    }
    const claims = await this.cs.getClaimWithCaseForUserId(cu.id, 10, 0);
    const cases = claims.map((cl) => cl.case_id);

    const now = new Date().toISOString();
    const q = this.supabase.from(this.table).select().order(
      'metadata->highlight_priority',
      { ascending: false }
    );
    // only published and within validity window. Allow nulls for open ranges.
    q.eq('status', 'PUBLISHED')
      .or(`valid_from.is.null,valid_from.lte.${now}`)
      .or(`valid_until.is.null,valid_until.gte.${now}`)
      .not('metadata', 'is', null);
    if (cases && cases.length) q.in('case_id', cases);
    if (limit) q.limit(limit);

    const { data, error } = await q;
    if (error) throw new Error(error.message);

    // If we don't have enough results, fetch more excluding the user's cases.
    const results: News[] = (data as News[]) || [];
    if (results.length >= limit) return results;

    const addNewsAmt = limit - results.length;
    const q2 = this.supabase.from(this.table).select().order(
      'metadata->highlight_priority',
      { ascending: false }
    ).order(
      'created_at',
      { ascending: true }
    );
    // apply same published + validity filters (allow nulls)
    q2.eq('status', 'PUBLISHED')
      .or(`valid_from.is.null,valid_from.lte.${now}`)
      .or(`valid_until.is.null,valid_until.gte.${now}`)
      .not('metadata', 'is', null);

    // Properly format NOT IN for PostgREST / Supabase. If cases is non-empty,
    // build a parenthesized, comma-separated list of quoted ids: ("id1","id2").
    if (cases && cases.length) {
      // Escape double quotes just in case and join
      const quoted = cases.map((c) => `"${String(c).replace(/"/g, '\\"')}"`).join(',');
      // Use the SQL-style operator form: not(case_id, 'in', '(<list>)') isn't supported,
      // so use the RPC-style filter string via .filter to create: not.in.("id1","id2").
      // Supabase JS client supports passing raw filter via .not(column, operator, value)
      // but value needs to be a comma-separated string without parentheses. We'll use
      // the .not filter with 'in' and a parenthesized list string as a fallback.
      // However, PostgREST expects not (column in (..)) which the client encodes as
      // not.in.(id1,id2) – to be safe, pass the string in the shape '("a","b")'.
      q2.not('case_id', 'in', `(${quoted})`);
    }

    if (addNewsAmt) q2.limit(addNewsAmt);

    const { data: extra, error: err2 } = await q2;
    if (err2) {
      // If the second query fails, just return whatever we had initially.
      return results;
    }

    const nv = (extra || []).concat(results || []);
    return nv.slice(0, limit);
  }

  async searchNews(
    config: {
      searchText?: string;
      limit?: number;
      page?: number;
      sortBy?: 'relevance' | 'newest' | 'oldest';
    } = {}
  ): Promise<{ data: News[]; count: number }> {
    const searchConfig = {
      limit: 10,
      page: 0,
      sortBy: 'relevance' as const,
      ...config,
    };

    const now = new Date().toISOString();

    let q = this.supabase.from(this.table).select('*', { count: 'exact' });

    q = q
      .eq('status', 'PUBLISHED')
      .or(`valid_from.is.null,valid_from.lte.${now}`)
      .or(`valid_until.is.null,valid_until.gte.${now}`)
      .not('metadata', 'is', null);

    if (searchConfig.searchText && searchConfig.searchText.trim()) {
      const searchTerm = searchConfig.searchText.trim();

      q = q.or(
        `metadata->>title.ilike.%${searchTerm}%,content.ilike.%${searchTerm}%,metadata->>plain_content.ilike.%${searchTerm}%`
      );
    }

    switch (searchConfig.sortBy) {
      case 'relevance':
        q = q.order('metadata->highlight_priority', { ascending: false });
        break;
      case 'newest':
        q = q.order('created_at', { ascending: false });
        break;
      case 'oldest':
        q = q.order('created_at', { ascending: true });
        break;
    }

    if (searchConfig.limit) {
      q = q.limit(searchConfig.limit);
    }
    if (searchConfig.page) {
      const { from, to } = this._getPagination(
        searchConfig.page,
        searchConfig.limit
      );
      q = q.range(from, to);
    }

    const { data, error, count } = await q;

    if (error) {
      throw new Error(error.message);
    }

    return {
      data: (data as News[]) || [],
      count: count ?? 0,
    };
  }
}
